import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { ArrowLeft, Plus, Trash2, Camera, Edit3, X, ChevronDown, ChevronUp, Search, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import AddCustomerModal from '../components/AddCustomerModal';
import CustomerSelectionModal from '../components/CustomerSelectionModal';
import EditCustomerModal from '../components/EditCustomerModal';
import ImageEditorModal from '../components/ImageEditorModal';
import SignatureModal from '../components/SignatureModal';
import { QuantityPickerModal, PurityPickerModal } from '../components/ModalPicker';
import { RatesSection } from '../components/RatesSection';
import { PaymentTypeSelector, CashPaymentModal, CardPaymentModal, UPIPaymentModal } from '../components/PaymentModals';
import { BankTransferModal, OldGoldModal } from '../components/PaymentModalsExtended';
import { MetalCreditModal } from '../components/MetalCreditModalNew';
import { PaymentSummary } from '../components/PaymentSummary';
import PaymentReminderCard from '../components/PaymentReminderCard';
import { uploadToCloudinary, uploadMultipleToCloudinary, isBase64DataUrl } from '../services/cloudinaryService';

const CreateApprovalStepper = ({ user, onLogout, editMode = false, existingApproval = null }) => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [sizePresets, setSizePresets] = useState([]);
  const [signatureNames, setSignatureNames] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Customer
  const [selectedCustomer, setSelectedCustomer] = useState(existingApproval?.customer_id || '');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  
  // Items
  // Initialize items with IDs if editing existing approval
  const [items, setItems] = useState(() => {
    if (existingApproval?.items) {
      return existingApproval.items.map((item, index) => ({
        ...item,
        id: item.id || `item-${Date.now()}-${index}`,
        gross_wt: item.gross_wt || item.weight_fixed || null,
        less_wt: item.less_wt || null,
        net_wt: item.net_wt || item.gross_wt || item.weight_fixed || null,
      }));
    }
    return [];
  });
  const [expandedItems, setExpandedItems] = useState({});
  const [showInternalRemark, setShowInternalRemark] = useState({});
  const [editingImage, setEditingImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  // Signature states
  const [managementSignature, setManagementSignature] = useState(existingApproval?.management_signature || null);
  const [managementSignatureName, setManagementSignatureName] = useState(
    existingApproval?.management_signature_name || (user?.username || '')
  );
  const [customerSignature, setCustomerSignature] = useState(existingApproval?.customer_signature || null);
  const [showManagementSignatureModal, setShowManagementSignatureModal] = useState(false);
  const [showCustomerSignatureModal, setShowCustomerSignatureModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdApprovalData, setCreatedApprovalData] = useState(null);
  
  // Rates
  const [rateMode, setRateMode] = useState(existingApproval?.rate_mode || 'single');
  const [ratePolicy, setRatePolicy] = useState(existingApproval?.rate_policy || 'none');
  const [lockDate, setLockDate] = useState(existingApproval?.lock_date || '');
  
  // GST and Tax settings
  const [taxScope, setTaxScope] = useState(existingApproval?.tax_scope || 'TAXABLE');
  const [gstApplicable, setGstApplicable] = useState(existingApproval?.gst_applicable ?? true);
  const [gstInputStyle, setGstInputStyle] = useState(existingApproval?.gst_input_style || 'EXCL');
  const [gstPercent, setGstPercent] = useState(existingApproval?.gst_percent || 3.0);
  
  // Master Data from Settings
  const [itemTypes, setItemTypes] = useState([]);
  const [purityOptions, setPurityOptions] = useState([]);
  
  // Fallback hardcoded arrays (if API fails)
  const fallbackItemTypes = ['Ring', 'Chain', 'Bangle', 'Bracelet', 'Necklace', 'Earring', 'Pendant', 'Nose Pin', 'Anklet', 'Mangalsutra', 'Haar', 'Other'];
  const fallbackPurities = ['24K', '22K', '18K'];
  
  const [purityMultipliers, setPurityMultipliers] = useState({ '24K': 1.0, '22K': 0.92, '18K': 0.76 });
  
  // Rate data
  const [base24kRate, setBase24kRate] = useState(existingApproval?.base_24k_rate || '');
  const [purityRates, setPurityRates] = useState(existingApproval?.purity_rates || {});
  const [purityRateLinks, setPurityRateLinks] = useState({});
  const [itemRates, setItemRates] = useState(existingApproval?.item_rates || {});
  
  // Approval Date
  const [approvalDate, setApprovalDate] = useState(existingApproval?.approval_date || new Date().toISOString().split('T')[0]);
  const [pendingApprovalDate, setPendingApprovalDate] = useState('');
  
  // Return/Collection Date
  const [returnDate, setReturnDate] = useState(existingApproval?.return_date || '');
  
  // Payment Reminder
  const [showPaymentReminder, setShowPaymentReminder] = useState(false);
  const [paymentReminderData, setPaymentReminderData] = useState(
    existingApproval?.reminder_date ? { enabled: true, due_at: existingApproval.reminder_date } : null
  );
  
  // Item Type Modal
  const [showItemTypeModal, setShowItemTypeModal] = useState(false);
  const [itemTypeModalTargetId, setItemTypeModalTargetId] = useState(null);
  const [itemTypeSearch, setItemTypeSearch] = useState('');
  
  // Review & Finalize
  const [showReviewPage, setShowReviewPage] = useState(false);
  
  // Payment
  const [payments, setPayments] = useState(existingApproval?.advance_payments || []);
  const [showPaymentTypeSelector, setShowPaymentTypeSelector] = useState(false);
  const [activePaymentModal, setActivePaymentModal] = useState(null);
  const [metalCreditBalance, setMetalCreditBalance] = useState(0);
  const [editingPaymentIndex, setEditingPaymentIndex] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePaymentIndex, setDeletePaymentIndex] = useState(null);
  
  // Modal pickers state
  const [activeModal, setActiveModal] = useState(null);
  const [modalItemId, setModalItemId] = useState(null);
  
  // Section states
  const [expandedSections, setExpandedSections] = useState({
    items: true,
    rates: false,
    return: false,
    payment: false,
    signatures: false,
    review: false
  });
  
  const [loading, setLoading] = useState(false);
  
  // Refs for scrolling
  const itemsRef = useRef(null);
  const ratesRef = useRef(null);
  const returnRef = useRef(null);
  const paymentRef = useRef(null);
  const reviewRef = useRef(null);

  useEffect(() => {
    loadCustomers();
    loadPresets();
    loadSignatureNames();
    
    // Auto-set management signature name to current user's username (only if not in edit mode)
    if (!editMode && user?.username && !managementSignatureName) {
      setManagementSignatureName(user.username);
    }
  }, []);

  const loadCustomers = async () => {
    try {
      const res = await axiosInstance.get('/customers');
      setCustomers(res.data);
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  const loadPresets = async () => {
    try {
      const [sizeRes, gstRes, itemTypesRes, purityRes] = await Promise.all([
        axiosInstance.get('/masters/size-presets'),
        axiosInstance.get('/masters/gst-config'),
        axiosInstance.get('/masters/item-types'),
        axiosInstance.get('/masters/purity-options')
      ]);
      setSizePresets(sizeRes.data);
      
      if (gstRes.data) {
        setGstPercent(gstRes.data.gst_percent || 3.0);
      }
      
      // Set master data (filter active only)
      const activeItemTypes = (itemTypesRes.data || [])
        .filter(item => item.is_active !== false)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .map(item => item.name);
      setItemTypes(activeItemTypes.length > 0 ? activeItemTypes : fallbackItemTypes);
      
      const activePurities = (purityRes.data || [])
        .filter(item => item.is_active !== false)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      
      if (activePurities.length > 0) {
        setPurityOptions(activePurities.map(p => p.name));
        // Build purity multipliers object
        const multipliers = {};
        activePurities.forEach(p => {
          multipliers[p.name] = p.multiplier;
        });
        setPurityMultipliers(multipliers);
      } else {
        setPurityOptions(fallbackPurities);
        setPurityMultipliers({ '24K': 1.0, '22K': 0.92, '18K': 0.76 });
      }
      
    } catch (err) {
      console.error('Error loading presets:', err);
      // Fallback to hardcoded arrays if API fails
      setItemTypes(fallbackItemTypes);
      setPurityOptions(fallbackPurities);
      setPurityMultipliers({ '24K': 1.0, '22K': 0.92, '18K': 0.76 });
    }
  };

  const loadSignatureNames = async () => {
    try {
      const res = await axiosInstance.get('/masters/signature-names');
      setSignatureNames(res.data);
    } catch (err) {
      console.error('Error loading signature names:', err);
      setSignatureNames([
        { id: 'sk', name: 'SK' },
        { id: 'mk', name: 'MK' },
        { id: 'av', name: 'AV' }
      ]);
    }
  };

  const getSizePresets = (itemType) => {
    const preset = sizePresets.find(p => p.item_type === itemType);
    return preset && preset.sizes ? preset.sizes : [];
  };

  // Auto-compute purity rates from base 24K rate
  const computePurityRates = (base24k) => {
    const computed = {};
    const links = {};
    Object.keys(purityMultipliers).forEach(purity => {
      if (purity !== '24K') {
        computed[purity] = Math.round(parseFloat(base24k) * purityMultipliers[purity]);
        links[purity] = true;
      }
    });
    setPurityRates(computed);
    setPurityRateLinks(links);
  };

  const handleBase24kChange = (value) => {
    setBase24kRate(value);
    if (value && !isNaN(value)) {
      computePurityRates(value);
    } else {
      setPurityRates({});
      setPurityRateLinks({});
    }
  };

  const togglePurityLink = (purity) => {
    setPurityRateLinks(prev => ({
      ...prev,
      [purity]: !prev[purity]
    }));
    
    if (!purityRateLinks[purity] && base24kRate) {
      setPurityRates(prev => ({
        ...prev,
        [purity]: Math.round(parseFloat(base24kRate) * purityMultipliers[purity])
      }));
    }
  };

  const handlePurityRateChange = (purity, value) => {
    setPurityRates(prev => ({
      ...prev,
      [purity]: value
    }));
    setPurityRateLinks(prev => ({
      ...prev,
      [purity]: false
    }));
  };

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.mobile.includes(customerSearch)
  );

  const handleCustomerAdded = (newCustomer) => {
    setCustomers([...customers, newCustomer]);
    setSelectedCustomer(newCustomer.id);
    setCustomerSearch(newCustomer.name);
    toast.success('Customer added and selected!');
  };

  const addItem = (afterIndex = null) => {
    // Generate unique ID using timestamp + random number to avoid collisions
    const newItemId = `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newItem = {
      id: newItemId,
      item_type: '',
      tag_number: '',
      photos: [],
      gross_wt: null,
      less_wt: null,
      net_wt: null,
      quantity: '1',
      purity: '22K',
      size: '',
      public_remark: '',
      internal_remark: '',
      collapsed: false
    };
    
    if (afterIndex !== null) {
      const newItems = [...items];
      newItems.splice(afterIndex + 1, 0, newItem);
      setItems(newItems);
    } else {
      setItems([...items, newItem]);
    }
    setExpandedItems({ ...expandedItems, [newItemId]: true });
  };

  const removeItem = (id) => {
    setItems(items.filter(item => item.id !== id));
    const newExpanded = { ...expandedItems };
    delete newExpanded[id];
    setExpandedItems(newExpanded);
  };

  const toggleItemExpand = (id) => {
    setExpandedItems({ ...expandedItems, [id]: !expandedItems[id] });
  };

  const collapseItem = (id) => {
    setExpandedItems({ ...expandedItems, [id]: false });
    const newItems = items.map(item =>
      item.id === id ? { ...item, collapsed: true } : item
    );
    setItems(newItems);
  };

  const toggleSection = (section) => {
    setExpandedSections({ ...expandedSections, [section]: !expandedSections[section] });
  };

  const scrollToSection = (ref, section) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setExpandedSections({ ...expandedSections, [section]: true });
  };

  const updateItem = (id, field, value) => {
    const newItems = items.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    );
    setItems(newItems);
  };

  const handlePhotoUpload = (id, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setModalItemId(id);
        setEditingImage({ imageData: reader.result });
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleImageSaved = (editedImage) => {
    if (modalItemId) {
      const item = items.find(i => i.id === modalItemId);
      if (item && item.photos.length < 3) {
        updateItem(modalItemId, 'photos', [...item.photos, editedImage]);
        toast.success('Photo added!');
      }
    }
    setEditingImage(null);
    setModalItemId(null);
  };

  const removePhoto = (id, photoIndex) => {
    const item = items.find(i => i.id === id);
    if (item) {
      const newPhotos = item.photos.filter((_, i) => i !== photoIndex);
      updateItem(id, 'photos', newPhotos);
    }
  };

  const toggleInternalRemark = (id) => {
    setShowInternalRemark({
      ...showInternalRemark,
      [id]: !showInternalRemark[id]
    });
  };

  // Approval Date Handling
  const handleApprovalDateChange = (newDate) => {
    // Allow all users to set any date - admin will review via approval page
    setApprovalDate(newDate);
  };

  // OTP handlers removed - backdating allowed without OTP

  // Return Date Quick Presets
  const addQuickReturnDays = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    setReturnDate(date.toISOString().split('T')[0]);
  };

  // Payment Handlers
  const handlePaymentSave = (paymentData) => {
    try {
      console.log('Payment data received:', paymentData);
      
      if (!paymentData) {
        throw new Error('Payment data is null or undefined');
      }

      if (editingPaymentIndex !== null) {
        // Edit mode
        const oldPayment = payments[editingPaymentIndex];
        const newPayments = [...payments];
        newPayments[editingPaymentIndex] = paymentData;
        setPayments(newPayments);
        
        // Adjust metal credit balance
        if (oldPayment && (oldPayment.mode === 'Metal Credit' || oldPayment.payment_type === 'Metal Credit')) {
          setMetalCreditBalance(prev => prev - (oldPayment.fine_wt || 0));
        } else if (oldPayment && (oldPayment.mode === 'Old Gold' || oldPayment.payment_type === 'Old Gold')) {
          const oldFineWt = oldPayment.items?.reduce((sum, item) => sum + (parseFloat(item.fine_wt) || 0), 0) || 0;
          setMetalCreditBalance(prev => prev - oldFineWt);
        }
        
        if (paymentData.mode === 'Metal Credit' || paymentData.payment_type === 'Metal Credit') {
          setMetalCreditBalance(prev => prev + (paymentData.fine_wt || 0));
        } else if (paymentData.mode === 'Old Gold' || paymentData.payment_type === 'Old Gold') {
          const newFineWt = paymentData.items?.reduce((sum, item) => sum + (parseFloat(item.fine_wt) || 0), 0) || 0;
          setMetalCreditBalance(prev => prev + newFineWt);
        }
        
        setEditingPaymentIndex(null);
        toast.success('Payment updated!');
      } else {
        // Add mode
        setPayments(prev => [...prev, paymentData]);
        
        if (paymentData.mode === 'Metal Credit' || paymentData.payment_type === 'Metal Credit') {
          setMetalCreditBalance(prev => prev + (paymentData.fine_wt || 0));
        } else if (paymentData.mode === 'Old Gold' || paymentData.payment_type === 'Old Gold') {
          const fineWt = paymentData.items?.reduce((sum, item) => sum + (parseFloat(item.fine_wt) || 0), 0) || 0;
          setMetalCreditBalance(prev => prev + fineWt);
        }
        
        toast.success('Payment added!');
      }
      
      setActivePaymentModal(null);
      setShowPaymentTypeSelector(false);
    } catch (error) {
      console.error('Error saving payment:', error);
      console.error('Payment data:', paymentData);
      console.error('Stack trace:', error.stack);
      toast.error(`Failed to save payment: ${error.message}`);
    }
  };

  const handlePaymentEdit = (index) => {
    setEditingPaymentIndex(index);
    const payment = payments[index];
    // Payment modals use 'mode' field, check both 'payment_type' and 'mode'
    const paymentType = payment.payment_type || payment.mode;
    
    if (paymentType === 'Cash') {
      setActivePaymentModal('cash');
    } else if (paymentType === 'Card') {
      setActivePaymentModal('card');
    } else if (paymentType === 'UPI') {
      setActivePaymentModal('upi');
    } else if (['IMPS', 'NEFT/RTGS', 'Cheque', 'Bank Transfer'].includes(paymentType)) {
      setActivePaymentModal('bank');
    } else if (paymentType === 'Old Gold') {
      setActivePaymentModal('old-gold');
    } else if (paymentType === 'Metal Credit' || paymentType === 'Metal (Fine Gold)') {
      setActivePaymentModal('metal');
    }
  };

  const handlePaymentDelete = (index) => {
    setDeletePaymentIndex(index);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    const payment = payments[deletePaymentIndex];
    const paymentType = payment.payment_type || payment.mode;
    
    // Adjust metal credit balance
    if (paymentType === 'Metal Credit' || paymentType === 'Metal (Fine Gold)') {
      setMetalCreditBalance(metalCreditBalance - (payment.fine_wt || 0));
    } else if (paymentType === 'Old Gold') {
      const fineWt = payment.items?.reduce((sum, item) => sum + (parseFloat(item.fine_wt) || 0), 0) || 0;
      setMetalCreditBalance(metalCreditBalance - fineWt);
    }
    
    setPayments(payments.filter((_, i) => i !== deletePaymentIndex));
    setShowDeleteConfirm(false);
    setDeletePaymentIndex(null);
    toast.success('Payment deleted');
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletePaymentIndex(null);
  };

  // Calculate Totals
  const calculateTotals = () => {
    let subtotal = 0;
    
    items.forEach(item => {
      const itemRate = rateMode === 'single' 
        ? (purityRates[item.purity] || parseFloat(base24kRate) || 0)
        : parseFloat(itemRates[item.id] || 0);
      
      let calcRate = itemRate;
      if (gstInputStyle === 'EXCL' && itemRate > 0) {
        calcRate = itemRate * 1.03;
      }
      
      const weight = parseFloat(item.net_wt || item.gross_wt || item.weight_fixed || 0);
      
      // Weight is total weight for all pieces — quantity is informational only, NOT a multiplier
      let itemSubtotal = weight * calcRate;
      subtotal += itemSubtotal;
    });
    
    const advancePaid = payments
      .filter(p => !['Old Gold', 'Metal Credit', 'Metal (Fine Gold)'].includes(p.payment_type || p.mode))
      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    
    const metalAdvance = metalCreditBalance;
    const totalAdvance = advancePaid + metalAdvance;
    const balance = subtotal - totalAdvance;
    const advancePercent = subtotal > 0 ? ((totalAdvance / subtotal) * 100).toFixed(1) : 0;
    
    return { subtotal, advancePaid, metalAdvance, totalAdvance, balance, advancePercent };
  };

  // Validation
  const getSectionStatus = (section) => {
    if (section === 'items') {
      if (items.length === 0) return 'incomplete';
      const allValid = items.every(item => 
        item.item_type && item.gross_wt
      );
      return allValid ? 'complete' : 'incomplete';
    }
    if (section === 'rates') {
      if (ratePolicy === 'none' || ratePolicy === 'rate-at-delivery') return 'complete';
      if (rateMode === 'single') {
        return base24kRate ? 'complete' : 'incomplete';
      }
      return 'complete';
    }
    if (section === 'return') {
      return returnDate ? 'complete' : 'warning';
    }
    if (section === 'payment') {
      return payments.length > 0 ? 'complete' : 'warning';
    }
    return 'complete';
  };

  const getStatusIcon = (status) => {
    if (status === 'complete') return <Check className="w-4 h-4 text-green-600" />;
    if (status === 'warning') return <AlertCircle className="w-4 h-4 text-amber-600" />;
    return <AlertCircle className="w-4 h-4 text-red-600" />;
  };

  // Submit Handler
  const handleSubmit = async () => {
    try {
      if (!selectedCustomer) {
        toast.error('Please select a customer');
        return;
      }
      
      if (items.length === 0) {
        toast.error('Please add at least one item');
        return;
      }
      
      setIsSubmitting(true);
      
      toast.info('Uploading images...', { duration: 2000 });
      
      // Collect all base64 images for batch upload
      const allBase64Photos = [];
      const photoMapping = [];
      
      items.forEach((item, itemIndex) => {
        if (item.photos && item.photos.length > 0) {
          item.photos.forEach((photo, photoIndex) => {
            if (isBase64DataUrl(photo)) {
              allBase64Photos.push(photo);
              photoMapping.push({ itemIndex, photoIndex, type: 'itemPhoto' });
            }
          });
        }
      });
      
      // Add signatures to batch if they're base64
      if (managementSignature && isBase64DataUrl(managementSignature)) {
        allBase64Photos.push(managementSignature);
        photoMapping.push({ type: 'managementSig' });
      }
      
      if (customerSignature && isBase64DataUrl(customerSignature)) {
        allBase64Photos.push(customerSignature);
        photoMapping.push({ type: 'customerSig' });
      }
      
      // Upload all photos in parallel with single signature request
      let uploadedUrls = [];
      if (allBase64Photos.length > 0) {
        try {
          const results = await uploadMultipleToCloudinary(allBase64Photos, 'approvals');
          uploadedUrls = results.map(r => r.url);
        } catch (e) {
          console.error('Batch upload failed:', e);
          uploadedUrls = allBase64Photos; // Keep originals if upload fails
        }
      }
      
      // Build URL map from uploaded results
      let uploadedManagementSig = managementSignature;
      let uploadedCustomerSig = customerSignature;
      const itemPhotoUrls = items.map(item => item.photos ? [...item.photos] : []);
      
      photoMapping.forEach((mapping, idx) => {
        const url = uploadedUrls[idx];
        if (mapping.type === 'itemPhoto') {
          itemPhotoUrls[mapping.itemIndex][mapping.photoIndex] = url;
        } else if (mapping.type === 'managementSig') {
          uploadedManagementSig = url;
        } else if (mapping.type === 'customerSig') {
          uploadedCustomerSig = url;
        }
      });
      
      // Process items with uploaded photo URLs
      const processedItems = items.map((item, itemIndex) => ({
        id: item.id || null,
        item_type: item.item_type || '',
        tag_number: item.tag_number || null,
        gross_wt: parseFloat(item.gross_wt) || 0,
        less_wt: parseFloat(item.less_wt) || 0,
        net_wt: parseFloat(item.net_wt || item.gross_wt) || 0,
        quantity: parseInt(item.quantity) || 1,
        purity: item.purity || '',
        size: item.size || null,
        photos: itemPhotoUrls[itemIndex] || [],
        public_remark: item.public_remark || null,
        internal_remark: item.internal_remark || null
      }));
      
      const approvalData = {
        customer_id: selectedCustomer,
        approval_date: approvalDate,
        return_date: returnDate || null,
        items: processedItems,
        rate_mode: rateMode || 'single',
        rate_policy: ratePolicy || 'none',
        lock_date: lockDate || null,
        tax_scope: taxScope || 'exclusive',
        gst_applicable: gstApplicable || false,
        gst_input_style: gstInputStyle || 'percentage',
        gst_percent: gstPercent || 3,
        base_24k_rate: base24kRate ? parseFloat(base24kRate) : null,
        purity_rates: purityRates || {},
        item_rates: itemRates || {},
        advance_payments: Array.isArray(payments) ? payments.map(p => ({
          payment_id: p.payment_id || `PAY-${Date.now()}`,
          mode: p.mode || p.payment_type || 'Cash',
          payment_type: p.payment_type || p.mode || 'Cash',
          amount: parseFloat(p.amount) || 0,
          date: p.date || approvalDate,
          reference: p.reference || null,
          note: p.note || null,
          items: Array.isArray(p.items) ? p.items : [],  // Old Gold items
          deposits: Array.isArray(p.deposits) ? p.deposits : [],  // Metal Credit deposits
          fine_grams: p.fine_grams || null,  // Old Gold/Metal Credit fine weight
          fine_wt: p.fine_wt || null,
          // Card fields
          card_last4: p.card_last4 || null,
          pos_ref: p.pos_ref || null,
          // UPI fields
          upi_ref: p.upi_ref || null,
          upi_id: p.upi_id || null,
          upi_handle: p.upi_handle || null,
          // Bank Transfer fields
          utr_ref: p.utr_ref || null,
          bank_name: p.bank_name || null,
          payer_acct_last4: p.payer_acct_last4 || null,
          // Cheque fields
          cheque_no: p.cheque_no || null,
          cheque_bank: p.cheque_bank || null,
          cheque_date: p.cheque_date || null,
          cheque_status: p.cheque_status || null
        })) : [],
        management_signature: uploadedManagementSig || null,
        management_signature_name: managementSignatureName || null,
        customer_signature: uploadedCustomerSig || null,
        reminder_date: paymentReminderData && paymentReminderData.enabled && paymentReminderData.due_at 
          ? paymentReminderData.due_at.split('T')[0] 
          : null,
        reminder_enabled: paymentReminderData?.enabled ?? false
      };
      
      console.log('Sending approval data:', approvalData);
      
      let res;
      if (editMode && existingApproval) {
        // Edit mode - send the full updated approval data
        // Backend will handle revision tracking and provisional changes
        res = await axiosInstance.put(`/approvals/${existingApproval.approval_id}`, approvalData);
        
        if (res.data?.is_provisional) {
          toast.success('Changes submitted! Provisional changes pending admin review.');
        } else {
          toast.success('Approval updated successfully!');
        }
        navigate(`/approvals/${existingApproval.approval_id}`);
        return;
      } else {
        // Create new approval - backend handles approval_status based on role
        res = await axiosInstance.post('/approvals', approvalData);
      }
      
      setCreatedApprovalData({
        approval_id: res.data.approval_id,
        customer_name: customers.find(c => c.id === selectedCustomer)?.name || 'Customer'
      });
      setShowReviewPage(false);
      setShowSuccessModal(true);
      toast.success(user?.role === 'Admin' ? 'Approval created successfully!' : 'Approval submitted for admin approval!');
    } catch (err) {
      console.error('Approval creation error:', err);
      console.error('Error details:', err.response?.data);
      
      // Handle error message properly
      let errorMessage = 'Error creating approval';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        // Check if detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignaturesComplete = async (managementSig, customerSig) => {
    setLoading(true);
    try {
      const approvalData = {
        customer_id: selectedCustomer,
        approval_date: approvalDate,
        return_date: returnDate || null,
        items: items.map(item => ({
          item_type: item.item_type,
          tag_number: item.tag_number || null,
          gross_wt: parseFloat(item.gross_wt) || 0,
          less_wt: parseFloat(item.less_wt) || 0,
          net_wt: parseFloat(item.net_wt || item.gross_wt) || 0,
          quantity: item.quantity,
          purity: item.purity,
          size: item.size || null,
          photos: item.photos,
          public_remark: item.public_remark || null,
          internal_remark: item.internal_remark || null
        })),
        rate_mode: rateMode,
        rate_policy: ratePolicy,
        lock_date: lockDate || null,
        tax_scope: taxScope,
        gst_applicable: gstApplicable,
        gst_input_style: gstInputStyle,
        gst_percent: gstPercent,
        base_24k_rate: base24kRate ? parseFloat(base24kRate) : null,
        purity_rates: purityRates,
        item_rates: itemRates,
        advance_payments: payments.map(p => ({
          payment_id: p.payment_id,
          payment_type: p.payment_type,
          amount: p.amount || null,
          date: p.date || approvalDate,
          reference: p.reference || null,
          deposits: p.deposits || []
        })),
        management_signature: managementSig,
        customer_signature: customerSig
      };
      
      console.log('Sending approval data:', approvalData);
      const res = await axiosInstance.post('/approvals', approvalData);
      
      setCreatedApprovalData({
        approval_id: res.data.approval_id,
        customer_name: customers.find(c => c.id === selectedCustomer)?.name || 'Customer'
      });
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Approval creation error:', err.response?.data);
      
      // Handle error message properly - same logic as handleSubmit
      let errorMessage = 'Error creating approval';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    // Navigate to the detail page of the created approval
    if (createdApprovalData?.approval_id) {
      navigate(`/approvals/${createdApprovalData.approval_id}`);
    }
  };

  const { subtotal, advancePaid, metalAdvance, totalAdvance, balance, advancePercent } = calculateTotals();

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-32">
      {/* Full-screen Loading Overlay during Approval Creation/Update */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 text-center shadow-2xl">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-200 border-t-amber-600 mx-auto mb-4"></div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              {editMode ? 'Updating Approval...' : 'Creating Approval...'}
            </h3>
            <p className="text-gray-600 text-sm">Please wait while we upload images and save your approval.</p>
            <p className="text-xs text-gray-400 mt-3">This may take a few seconds</p>
          </div>
        </div>
      )}

      {/* Sticky Header with Stepper */}
      <div className="sticky top-0 z-40 bg-white shadow-md border-b-2 border-amber-200">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => navigate(editMode ? `/approvals/${existingApproval?.approval_id}` : '/approvals')} className="p-2 hover:bg-amber-100 rounded-xl transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-bold text-gray-800">
              {editMode ? `Edit Approval - ${existingApproval?.approval_id}` : 'Create Approval'}
            </h1>
          </div>
          
          {/* Mini Stepper */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
            <button
              onClick={() => scrollToSection(itemsRef, 'items')}
              className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-2 transition-all ${
                expandedSections.items ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <span>Items ({items.length})</span>
              {getStatusIcon(getSectionStatus('items'))}
            </button>
            
            <button
              onClick={() => scrollToSection(ratesRef, 'rates')}
              className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-2 transition-all ${
                expandedSections.rates ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <span>Rates</span>
              {getStatusIcon(getSectionStatus('rates'))}
            </button>
            
            <button
              onClick={() => scrollToSection(returnRef, 'return')}
              className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-2 transition-all ${
                expandedSections.return ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <span>Return Date</span>
              {getStatusIcon(getSectionStatus('return'))}
            </button>
            
            <button
              onClick={() => scrollToSection(paymentRef, 'payment')}
              className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-2 transition-all ${
                expandedSections.payment ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <span>Payment</span>
              {getStatusIcon(getSectionStatus('payment'))}
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-3">
        {/* Merged Header - Customer & Approval Date */}
        <div className="premium-card border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Approval Date */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Approval Date *</label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    className="input-field text-sm flex-1"
                    value={approvalDate}
                    onChange={(e) => handleApprovalDateChange(e.target.value)}
                  />
                  {approvalDate < new Date().toISOString().split('T')[0] && (
                    <span className="px-2 py-1 bg-amber-200 text-amber-800 text-xs font-semibold rounded whitespace-nowrap">
                      Back-dated
                    </span>
                  )}
                </div>
              </div>
              
              {/* Customer Selector */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Customer *</label>
                {selectedCustomer ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-white border-2 border-green-200 rounded-lg px-3 py-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1">
                          <span className="text-sm font-semibold text-gray-800">
                            {customers.find(c => c.id === selectedCustomer)?.name || 'Selected'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const customer = customers.find(c => c.id === selectedCustomer);
                              setEditingCustomer(customer);
                              setShowEditCustomerModal(true);
                            }}
                            className="text-xs text-blue-600 font-semibold hover:text-blue-700 flex items-center gap-1"
                          >
                            <Edit3 className="w-3 h-3" />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowCustomerModal(true)}
                            className="text-xs text-amber-600 font-semibold hover:text-amber-700"
                          >
                            Change
                          </button>
                        </div>
                      </div>
                      {/* Customer Details */}
                      <div className="space-y-1 text-xs">
                        {customers.find(c => c.id === selectedCustomer)?.mobile && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Mobile:</span>
                            <span className="font-semibold text-gray-800">{customers.find(c => c.id === selectedCustomer)?.mobile}</span>
                          </div>
                        )}
                        {customers.find(c => c.id === selectedCustomer)?.alternate_mobile && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Alt Mobile:</span>
                            <span className="font-semibold text-gray-800">{customers.find(c => c.id === selectedCustomer)?.alternate_mobile}</span>
                          </div>
                        )}
                        {customers.find(c => c.id === selectedCustomer)?.email && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Email:</span>
                            <span className="font-semibold text-gray-800 truncate ml-2">{customers.find(c => c.id === selectedCustomer)?.email}</span>
                          </div>
                        )}
                        {customers.find(c => c.id === selectedCustomer)?.address && (
                          <div className="flex flex-col gap-0.5 mt-1 pt-1 border-t border-gray-200">
                            <span className="text-gray-600">Address:</span>
                            <span className="font-semibold text-gray-800 break-words">{customers.find(c => c.id === selectedCustomer)?.address}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowCustomerModal(true)}
                    className="w-full py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Search className="w-4 h-4" />
                    Select Customer
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 1: ITEMS */}
        <div className="premium-card border-2 border-amber-200" ref={itemsRef}>
          <div
            className="p-4 bg-gradient-to-r from-amber-100 to-orange-100 cursor-pointer flex justify-between items-center"
            onClick={() => toggleSection('items')}
          >
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-gray-800">Items</h2>
              <span className="bg-white text-amber-600 font-bold text-xs px-2 py-1 rounded-full">{items.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  addItem();
                }}
                className="text-xs font-semibold text-amber-700 hover:text-amber-900 flex items-center gap-1 px-2 py-1 bg-white rounded-lg"
              >
                <Plus className="w-3 h-3" />
                Add Item
              </button>
              {expandedSections.items ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </div>

          {expandedSections.items && (
            <div className="p-4 space-y-3">
              {items.length === 0 ? (
                <div className="text-center py-8 bg-amber-50 rounded-xl">
                  <p className="text-sm text-gray-600">No items yet. Click &quot;+ Add Item&quot; above</p>
                </div>
              ) : (
                items.map((item, itemIndex) => (
                  <div key={item.id} className="space-y-2">
                    <div className="border-2 border-amber-200 rounded-xl overflow-hidden bg-white">
                      {/* Collapsed Preview */}
                      {!expandedItems[item.id] ? (
                        <div className="p-3 bg-amber-50">
                          <div className="flex justify-between items-start mb-1">
                            <span className="bg-amber-600 text-white text-xs px-2 py-0.5 rounded-full font-bold">#{itemIndex + 1}</span>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => toggleItemExpand(item.id)}
                                className="p-1 hover:bg-amber-100 rounded"
                              >
                                <ChevronDown className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeItem(item.id)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap text-xs leading-relaxed">
                              <span className="font-semibold text-gray-900">{item.item_type}</span>
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-700">
                                {item.net_wt ? `${item.net_wt}g` : `${item.gross_wt || 0}g`}
                              </span>
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-700">Qty {item.quantity}</span>
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-700">{item.purity}</span>
                              {item.size && (
                                <>
                                  <span className="text-gray-500">•</span>
                                  <span className="text-gray-700">Sz {item.size}</span>
                                </>
                              )}
                              {item.tag_number && (
                                <>
                                  <span className="text-gray-500">•</span>
                                  <span className="text-gray-700">Tag:{item.tag_number}</span>
                                </>
                              )}
                              {item.public_remark && (
                                <>
                                  <span className="text-gray-500">•</span>
                                  <span className="text-gray-600 italic">
                                    {item.public_remark.length > 30 ? `${item.public_remark.substring(0, 30)}...` : item.public_remark}
                                  </span>
                                </>
                              )}
                              {item.photos && item.photos.length > 0 && (
                                <>
                                  <span className="text-gray-500">•</span>
                                  <span className="text-gray-700">{item.photos.length} 📷</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Expanded Header */}
                          <div className="p-3 bg-amber-50 flex justify-between items-center">
                            <div className="flex items-center gap-2 flex-1">
                              <span className="bg-amber-600 text-white text-xs px-2 py-0.5 rounded-full font-bold">#{itemIndex + 1}</span>
                              <div>
                                <h4 className="font-semibold text-sm">{item.item_type}</h4>
                                <p className="text-xs text-gray-600">
                                  {item.net_wt ? `Net: ${item.net_wt}g` : `${item.gross_wt || 0}g`} | {item.purity}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => toggleItemExpand(item.id)}
                                className="p-1"
                              >
                                <ChevronUp className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeItem(item.id)}
                                className="p-1 text-red-500"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Expanded Form */}
                          <div className="p-3 space-y-3 text-sm">
                            {/* Item Type */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Item Type</label>
                              <button
                                type="button"
                                onClick={() => {
                                  setItemTypeModalTargetId(item.id);
                                  setShowItemTypeModal(true);
                                }}
                                className="input-field w-full text-sm text-left flex items-center justify-between hover:bg-amber-50 transition-colors"
                              >
                                <span>{item.item_type}</span>
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              </button>
                            </div>
                            
                            {/* Weight Inputs - Gross, Less, Net */}
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">Gross Wt (g)</label>
                                <input
                                  type="number"
                                  step="0.001"
                                  className="input-field w-full text-sm"
                                  value={item.gross_wt || ''}
                                  onChange={(e) => {
                                    const grossVal = e.target.value;
                                    const gross = parseFloat(grossVal) || 0;
                                    const less = parseFloat(item.less_wt) || 0;
                                    const netWt = gross > 0 ? (gross - less).toFixed(3) : '';
                                    setItems(items.map(i => i.id === item.id ? { ...i, gross_wt: grossVal, net_wt: netWt } : i));
                                  }}
                                  data-testid={`item-${itemIndex}-gross-wt`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">Less Wt (g)</label>
                                <input
                                  type="number"
                                  step="0.001"
                                  className="input-field w-full text-sm"
                                  value={item.less_wt || ''}
                                  onChange={(e) => {
                                    const lessVal = e.target.value;
                                    const gross = parseFloat(item.gross_wt) || 0;
                                    const less = parseFloat(lessVal) || 0;
                                    const netWt = gross > 0 ? (gross - less).toFixed(3) : '';
                                    setItems(items.map(i => i.id === item.id ? { ...i, less_wt: lessVal, net_wt: netWt } : i));
                                  }}
                                  data-testid={`item-${itemIndex}-less-wt`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">Net Wt (g)</label>
                                <input
                                  type="number"
                                  step="0.001"
                                  className="input-field w-full text-sm bg-gray-50"
                                  value={item.net_wt || ''}
                                  readOnly
                                  data-testid={`item-${itemIndex}-net-wt`}
                                />
                              </div>
                            </div>

                            {/* Tag Number */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Tag Number</label>
                              <input
                                type="text"
                                className="input-field w-full text-sm"
                                value={item.tag_number || ''}
                                onChange={(e) => updateItem(item.id, 'tag_number', e.target.value.toUpperCase())}
                                placeholder="Enter tag/serial number"
                              />
                            </div>

                            {/* Quantity | Purity */}
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">Quantity</label>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setModalItemId(item.id);
                                    setActiveModal('quantity');
                                  }}
                                  className="input-field w-full text-sm text-left"
                                >
                                  {item.quantity || '1'}
                                </button>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">Purity</label>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setModalItemId(item.id);
                                    setActiveModal('purity');
                                  }}
                                  className="input-field w-full text-sm text-left"
                                >
                                  {item.purity || '22K'}
                                </button>
                              </div>
                            </div>

                            {/* Size */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Size</label>
                              {getSizePresets(item.item_type).length > 0 && (
                                <div className="overflow-x-auto scrollbar-hide mb-2">
                                  <div className="flex gap-1 pb-1" style={{ minWidth: 'min-content' }}>
                                    {getSizePresets(item.item_type).map((preset) => (
                                      <button
                                        key={preset}
                                        type="button"
                                        onClick={() => updateItem(item.id, 'size', preset)}
                                        className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                                          item.size === preset
                                            ? 'bg-amber-500 text-white'
                                            : 'bg-amber-100 text-amber-700'
                                        }`}
                                      >
                                        {preset}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <input
                                type="text"
                                className="input-field w-full text-sm"
                                value={item.size}
                                onChange={(e) => updateItem(item.id, 'size', e.target.value)}
                                placeholder="Enter size"
                              />
                            </div>

                            {/* Photos */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Photos</label>
                              <div className="flex gap-2">
                                {item.photos && item.photos.map((photo, i) => (
                                  <div key={i} className="relative group">
                                    <img src={photo} alt="Item" className="w-16 h-16 object-cover rounded border" />
                                    <button
                                      type="button"
                                      onClick={() => removePhoto(item.id, i)}
                                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                                {(!item.photos || item.photos.length < 3) && (
                                  <label className="w-16 h-16 border-2 border-dashed border-amber-300 rounded flex items-center justify-center cursor-pointer">
                                    <input
                                      type="file"
                                      accept="image/jpeg,image/jpg,image/png,image/heic,image/heif"
                                      onChange={(e) => handlePhotoUpload(item.id, e)}
                                      className="hidden"
                                    />
                                    <Camera className="w-5 h-5 text-amber-600" />
                                  </label>
                                )}
                              </div>
                            </div>

                            {/* Public Remark */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Public Remark</label>
                              <textarea
                                className="input-field w-full text-sm"
                                rows="2"
                                value={item.public_remark}
                                onChange={(e) => updateItem(item.id, 'public_remark', e.target.value)}
                              />
                            </div>

                            {/* Internal Remark */}
                            <div>
                              {!showInternalRemark[item.id] ? (
                                <button
                                  type="button"
                                  onClick={() => toggleInternalRemark(item.id)}
                                  className="text-xs font-semibold text-gray-600 flex items-center gap-1"
                                >
                                  <Plus className="w-3 h-3" />
                                  Add Internal Remark
                                </button>
                              ) : (
                                <div>
                                  <label className="block text-xs font-semibold text-gray-700 mb-1">Internal Remark</label>
                                  <textarea
                                    className="input-field w-full text-sm bg-yellow-50"
                                    rows="2"
                                    value={item.internal_remark}
                                    onChange={(e) => updateItem(item.id, 'internal_remark', e.target.value)}
                                  />
                                </div>
                              )}
                            </div>

                            <button
                              type="button"
                              onClick={() => collapseItem(item.id)}
                              className="btn-secondary w-full text-sm py-2"
                            >
                              Save & Collapse
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Quick Add Button */}
                    {!expandedItems[item.id] && itemIndex === items.length - 1 && (
                      <button
                        type="button"
                        onClick={() => addItem()}
                        className="w-full py-2.5 text-xs font-semibold text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg flex items-center justify-center gap-1 transition-colors border-2 border-dashed border-amber-300"
                      >
                        <Plus className="w-4 h-4" />
                        Add Another Item
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* SECTION 2: RATES */}
        <div ref={ratesRef} className="premium-card border-2 border-amber-200">
          <div
            className="p-4 bg-gradient-to-r from-amber-100 to-orange-100 cursor-pointer flex justify-between items-center"
            onClick={() => toggleSection('rates')}
          >
            <h2 className="text-base font-bold text-gray-800">Rates & Policy</h2>
            {expandedSections.rates ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>

          {expandedSections.rates && (
            <RatesSection
              taxScope={taxScope}
              setTaxScope={setTaxScope}
              gstApplicable={gstApplicable}
              setGstApplicable={setGstApplicable}
              gstInputStyle={gstInputStyle}
              setGstInputStyle={setGstInputStyle}
              gstPercent={gstPercent}
              rateMode={rateMode}
              setRateMode={setRateMode}
              ratePolicy={ratePolicy}
              setRatePolicy={setRatePolicy}
              lockDate={lockDate}
              setLockDate={setLockDate}
              base24kRate={base24kRate}
              handleBase24kChange={handleBase24kChange}
              purityRates={purityRates}
              handlePurityRateChange={handlePurityRateChange}
              purityRateLinks={purityRateLinks}
              togglePurityLink={togglePurityLink}
              purityMultipliers={purityMultipliers}
              items={items}
              itemRates={itemRates}
              setItemRates={setItemRates}
            />
          )}
        </div>

        {/* SECTION 3: RETURN DATE */}
        <div ref={returnRef} className="premium-card border-2 border-amber-200">
          <div
            className="p-4 bg-gradient-to-r from-amber-100 to-orange-100 cursor-pointer flex justify-between items-center"
            onClick={() => toggleSection('return')}
          >
            <h2 className="text-base font-bold text-gray-800">Return/Collection Date</h2>
            {expandedSections.return ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>

          {expandedSections.return && (
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">When will customer return approval?</label>
                <div className="flex gap-2 mb-2">
                  <button
                    onClick={() => addQuickReturnDays(2)}
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-200"
                  >
                    +2 days
                  </button>
                  <button
                    onClick={() => addQuickReturnDays(3)}
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-200"
                  >
                    +3 days
                  </button>
                  <button
                    onClick={() => addQuickReturnDays(7)}
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-200"
                  >
                    +7 days
                  </button>
                  <button
                    onClick={() => addQuickReturnDays(14)}
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-200"
                  >
                    +14 days
                  </button>
                </div>
                <input
                  type="date"
                  className="input-field text-sm w-full"
                  value={returnDate}
                  onChange={(e) => setReturnDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
                {returnDate && (
                  <p className="text-xs text-gray-600 mt-2">
                    Customer will return approval by {new Date(returnDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* SECTION 4: PAYMENT */}
        <div ref={paymentRef} className="premium-card border-2 border-amber-200">
          <div
            className="p-4 bg-gradient-to-r from-amber-100 to-orange-100 cursor-pointer flex justify-between items-center"
            onClick={() => toggleSection('payment')}
          >
            <h2 className="text-base font-bold text-gray-800">Payment & Advance</h2>
            {expandedSections.payment ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>

          {expandedSections.payment && (
            <div className="p-4 space-y-3">
              {/* Payment Summary */}
              <PaymentSummary
                payments={payments}
                metalCreditBalance={metalCreditBalance}
                subtotal={subtotal}
                onEdit={handlePaymentEdit}
                onDelete={handlePaymentDelete}
              />

              {/* Add Payment Button */}
              <button
                onClick={() => setShowPaymentTypeSelector(true)}
                className="w-full py-3 bg-gradient-to-r from-green-400 to-emerald-400 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Payment
              </button>

              {/* Totals Display */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border-2 border-amber-200">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Subtotal:</span>
                    <span className="font-bold text-gray-900">₹{subtotal.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-green-700">
                    <span>Cash Advance:</span>
                    <span className="font-semibold">₹{advancePaid.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-amber-700">
                    <span>Metal Credit:</span>
                    <span className="font-semibold">{metalAdvance.toFixed(3)}g</span>
                  </div>
                  <div className="border-t-2 border-amber-300 pt-2 flex justify-between">
                    <span className="font-semibold text-gray-700">Balance:</span>
                    <span className="font-bold text-lg text-red-600">₹{balance.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="text-center">
                    <span className="text-xs bg-amber-200 text-amber-800 px-3 py-1 rounded-full font-semibold">
                      {advancePercent}% Advance Paid
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Reminder Card */}
              <PaymentReminderCard
                isExpanded={showPaymentReminder}
                onToggle={() => setShowPaymentReminder(!showPaymentReminder)}
                advancePaid={totalAdvance}
                requiredAdvance={subtotal * 0.8}
                selectedCustomer={selectedCustomer}
                customers={customers}
                orderDate={approvalDate}
                onReminderChange={setPaymentReminderData}
                initialData={paymentReminderData}
              />
            </div>
          )}
        </div>

        {/* Signatures Section */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border-2 border-gray-200">
          <div
            className="bg-gradient-to-r from-blue-100 to-indigo-100 p-4 flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('signatures')}
          >
            <h2 className="text-base font-bold text-gray-800">Signatures</h2>
            {expandedSections.signatures ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>

          {expandedSections.signatures && (
            <div className="p-4 space-y-4">
              {/* Management Signature */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Management Signature
                </label>
                <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
                  {managementSignature ? (
                    <div className="relative p-4">
                      <img src={managementSignature} alt="Management Signature" className="w-full h-32 object-contain border border-gray-200 rounded" />
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => setShowManagementSignatureModal(true)}
                          className="flex-1 px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 font-semibold"
                        >
                          Edit Signature
                        </button>
                        <button
                          onClick={() => setManagementSignature(null)}
                          className="px-4 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 font-semibold"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowManagementSignatureModal(true)}
                      className="w-full p-8 flex flex-col items-center justify-center gap-3 hover:bg-amber-50 transition-colors"
                    >
                      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                        <span className="text-3xl">✍️</span>
                      </div>
                      <span className="text-amber-600 font-semibold">Tap to Add Management Signature</span>
                      <span className="text-xs text-gray-500">Opens fullscreen signing pad</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Customer Signature */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Customer Signature
                </label>
                <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
                  {customerSignature ? (
                    <div className="relative p-4">
                      <img src={customerSignature} alt="Customer Signature" className="w-full h-32 object-contain border border-gray-200 rounded" />
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => setShowCustomerSignatureModal(true)}
                          className="flex-1 px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 font-semibold"
                        >
                          Edit Signature
                        </button>
                        <button
                          onClick={() => setCustomerSignature(null)}
                          className="px-4 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 font-semibold"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowCustomerSignatureModal(true)}
                      className="w-full p-8 flex flex-col items-center justify-center gap-3 hover:bg-blue-50 transition-colors"
                    >
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-3xl">✍️</span>
                      </div>
                      <span className="text-blue-600 font-semibold">Tap to Add Customer Signature</span>
                      <span className="text-xs text-gray-500">Opens fullscreen signing pad</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Review & Submit Button */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-amber-200 p-4 shadow-lg z-30">
          <button
            onClick={() => setShowReviewPage(true)}
            disabled={!selectedCustomer || items.length === 0}
            className="w-full py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Review & Finalize Approval →
          </button>
        </div>
      </div>

      {/* Modals */}
      {showCustomerModal && (
        <CustomerSelectionModal
          onClose={() => setShowCustomerModal(false)}
          customers={customers}
          onCustomerSelected={(customer) => {
            // Add customer to list if not already present
            if (!customers.find(c => c.id === customer.id)) {
              setCustomers([...customers, customer]);
            }
            setSelectedCustomer(customer.id || customer.customer_id);
            setShowCustomerModal(false);
            toast.success('Customer selected!');
          }}
          selectedCustomerId={selectedCustomer}
        />
      )}

      {showItemTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-2xl sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b-2 border-amber-200 flex justify-between items-center bg-gradient-to-r from-amber-100 to-orange-100">
              <h3 className="text-lg font-bold text-gray-800">Select Item Type</h3>
              <button 
                onClick={() => {
                  setShowItemTypeModal(false);
                  setItemTypeSearch('');
                  setItemTypeModalTargetId(null);
                }}
                className="p-2 hover:bg-amber-200 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  className="input-field w-full pl-10"
                  placeholder="Search item types..."
                  value={itemTypeSearch}
                  onChange={(e) => setItemTypeSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {itemTypes
                  .filter(type => type.toLowerCase().includes(itemTypeSearch.toLowerCase()))
                  .map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        if (itemTypeModalTargetId) {
                          updateItem(itemTypeModalTargetId, 'item_type', type);
                        }
                        setShowItemTypeModal(false);
                        setItemTypeSearch('');
                        setItemTypeModalTargetId(null);
                      }}
                      className="p-4 border-2 border-amber-200 rounded-lg hover:bg-amber-50 transition-all hover:border-amber-400 hover:shadow-md"
                    >
                      <div className="text-sm font-semibold text-gray-800">{type}</div>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <QuantityPickerModal
        isOpen={activeModal === 'quantity'}
        onClose={() => {
          setActiveModal(null);
          setModalItemId(null);
        }}
        onSelect={(value) => {
          if (modalItemId) {
            updateItem(modalItemId, 'quantity', value);
          }
        }}
        currentValue={modalItemId ? items.find(i => i.id === modalItemId)?.quantity : '1'}
      />

      <PurityPickerModal
        isOpen={activeModal === 'purity'}
        onClose={() => {
          setActiveModal(null);
          setModalItemId(null);
        }}
        onSelect={(value) => {
          if (modalItemId) {
            updateItem(modalItemId, 'purity', value);
          }
        }}
        currentValue={modalItemId ? items.find(i => i.id === modalItemId)?.purity : '22K'}
        purities={Object.keys(purityMultipliers)}
      />

      {editingImage && (
        <ImageEditorModal
          imageData={editingImage.imageData}
          onSave={handleImageSaved}
          onClose={() => {
            setEditingImage(null);
            setModalItemId(null);
          }}
        />
      )}

      {previewImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
          onClick={() => setPreviewImage(null)}
        >
          <img src={previewImage} alt="Preview" className="max-w-full max-h-full" />
        </div>
      )}

      {/* Payment Modals */}
      <PaymentTypeSelector
        isOpen={showPaymentTypeSelector}
        onClose={() => setShowPaymentTypeSelector(false)}
        onSelectType={(type) => {
          setShowPaymentTypeSelector(false);
          setActivePaymentModal(type);
        }}
      />

      <CashPaymentModal
        isOpen={activePaymentModal === 'cash'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
      />

      <CardPaymentModal
        isOpen={activePaymentModal === 'card'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
      />

      <UPIPaymentModal
        isOpen={activePaymentModal === 'upi'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
      />

      <BankTransferModal
        isOpen={activePaymentModal === 'bank'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
      />

      <OldGoldModal
        isOpen={activePaymentModal === 'old-gold'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
      />

      <MetalCreditModal
        isOpen={activePaymentModal === 'metal'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
      />

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Delete Payment?</h3>
            <p className="text-sm text-gray-600 mb-4">Are you sure you want to delete this payment entry?</p>
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-2 bg-red-500 text-white rounded-lg font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Modal for Backdating */}
      {/* Review Page - Will be implemented similar to Order Review */}
      {showReviewPage && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          <div className="sticky top-0 bg-gradient-to-r from-amber-100 to-orange-100 border-b-2 border-amber-200 p-4 shadow-md">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">Review Approval</h2>
              <button 
                onClick={() => setShowReviewPage(false)}
                className="p-2 hover:bg-amber-200 rounded-lg"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-4 space-y-4 max-w-3xl mx-auto pb-32">
            {/* Customer & Date Info */}
            <div className="premium-card border-2 border-amber-200 p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-gray-800">Approval Information</h3>
                <button 
                  onClick={() => setShowReviewPage(false)}
                  className="text-xs text-amber-600 font-semibold hover:text-amber-700"
                >
                  ✏️ Edit
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600 text-xs mb-2">Customer Information</p>
                  <div className="space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-xs">Name:</span>
                      <span className="font-semibold">{customers.find(c => c.id === selectedCustomer)?.name || 'Not selected'}</span>
                    </div>
                    {customers.find(c => c.id === selectedCustomer)?.mobile && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 text-xs">Mobile:</span>
                        <span className="font-semibold">{customers.find(c => c.id === selectedCustomer)?.mobile}</span>
                      </div>
                    )}
                    {customers.find(c => c.id === selectedCustomer)?.alternate_mobile && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 text-xs">Alt Mobile:</span>
                        <span className="font-semibold">{customers.find(c => c.id === selectedCustomer)?.alternate_mobile}</span>
                      </div>
                    )}
                    {customers.find(c => c.id === selectedCustomer)?.email && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 text-xs">Email:</span>
                        <span className="font-semibold text-xs break-all">{customers.find(c => c.id === selectedCustomer)?.email}</span>
                      </div>
                    )}
                    {customers.find(c => c.id === selectedCustomer)?.address && (
                      <div className="flex flex-col gap-1 pt-1 border-t border-gray-200">
                        <span className="text-gray-600 text-xs">Address:</span>
                        <span className="font-semibold text-xs break-words">{customers.find(c => c.id === selectedCustomer)?.address}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <p className="text-gray-600 text-xs">Approval Date</p>
                    <p className="font-semibold">
                      {new Date(approvalDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {approvalDate < new Date().toISOString().split('T')[0] && (
                        <span className="ml-2 px-2 py-0.5 bg-amber-200 text-amber-800 text-xs rounded">Back-dated</span>
                      )}
                    </p>
                  </div>
                  {returnDate && (
                    <div>
                      <p className="text-gray-600 text-xs">Expected Return Date</p>
                      <p className="font-semibold text-blue-700">
                        {new Date(returnDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        <span className="ml-2 text-xs text-gray-500">
                          ({Math.ceil((new Date(returnDate) - new Date(approvalDate)) / (1000 * 60 * 60 * 24))} days)
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Weight Summary Card */}
            <div className="premium-card border-2 border-purple-200 p-4 bg-gradient-to-r from-purple-50 to-indigo-50">
              <h3 className="text-sm font-bold text-gray-800 mb-3">Weight Summary</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-xs text-gray-600">Total Items</p>
                  <p className="text-2xl font-bold text-purple-700">{items.length}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-600">Gross Weight</p>
                  <p className="text-2xl font-bold text-gray-700">
                    {items.reduce((sum, item) => {
                      return sum + (parseFloat(item.gross_wt) || 0);
                    }, 0).toFixed(2)}g
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-600">Net Weight</p>
                  <p className="text-2xl font-bold text-amber-700">
                    {items.reduce((sum, item) => {
                      return sum + (parseFloat(item.net_wt || item.gross_wt) || 0);
                    }, 0).toFixed(2)}g
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-600">Fine Weight</p>
                  <p className="text-2xl font-bold text-indigo-700">
                    {items.reduce((sum, item) => {
                      const netWt = parseFloat(item.net_wt || item.gross_wt) || 0;
                      const purityMultiplier = item.purity === '24K' ? 1.0 : item.purity === '22K' ? 0.916 : item.purity === '18K' ? 0.75 : 0.916;
                      return sum + (netWt * purityMultiplier);
                    }, 0).toFixed(2)}g
                  </p>
                </div>
              </div>
            </div>

            {/* Items Summary */}
            <div className="premium-card border-2 border-amber-200 p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-gray-800">Items ({items.length})</h3>
                <button 
                  onClick={() => {
                    setShowReviewPage(false);
                    scrollToSection(itemsRef, 'items');
                  }}
                  className="text-xs text-amber-600 font-semibold hover:text-amber-700"
                >
                  ✏️ Edit
                </button>
              </div>
              <div className="space-y-3">
                {items.map((item, idx) => {
                  // Weight is total weight for all pieces — quantity is informational only, NOT a multiplier
                  const grossWt = parseFloat(item.gross_wt) || 0;
                  const netWt = parseFloat(item.net_wt || item.gross_wt) || 0;
                  const purityMultiplier = item.purity === '24K' ? 1.0 : item.purity === '22K' ? 0.916 : item.purity === '18K' ? 0.75 : 0.916;
                  const fineWt = netWt * purityMultiplier;
                  
                  return (
                    <div key={item.id} className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 text-xs">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="bg-amber-600 text-white px-2 py-0.5 rounded-full font-bold">#{idx + 1}</span>
                          <span className="font-semibold text-sm">{item.item_type}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-600">Fine Wt</p>
                          <p className="font-bold text-purple-700">{fineWt.toFixed(2)}g</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                        <div><span className="text-gray-600">Gross Wt:</span> <span className="font-medium">{item.gross_wt || 0}g</span></div>
                        <div><span className="text-gray-600">Less Wt:</span> <span className="font-medium">{item.less_wt || 0}g</span></div>
                        <div><span className="text-gray-600">Net Wt:</span> <span className="font-medium text-amber-700">{item.net_wt || item.gross_wt || 0}g</span></div>
                        <div><span className="text-gray-600">Purity:</span> <span className="font-medium">{item.purity}</span></div>
                        <div><span className="text-gray-600">Quantity:</span> <span className="font-medium">{qty}</span></div>
                        
                        {item.size && (
                          <div><span className="text-gray-600">Size:</span> <span className="font-medium">{item.size}</span></div>
                        )}
                        
                        {item.tag_number && (
                          <div className="col-span-2"><span className="text-gray-600">Tag #:</span> <span className="font-medium">{item.tag_number}</span></div>
                        )}
                        
                        {item.public_remark && (
                          <div className="col-span-2 sm:col-span-3"><span className="text-gray-600">Public Remarks:</span> <span className="font-medium">{item.public_remark}</span></div>
                        )}
                      </div>

                      {item.photos && item.photos.length > 0 && (
                        <div className="border-t border-amber-300 pt-2 mt-2">
                          <p className="text-gray-600 mb-1 font-medium">Photos ({item.photos.length}):</p>
                          <div className="flex gap-2 flex-wrap">
                            {item.photos.map((img, imgIdx) => (
                              <img
                                key={imgIdx}
                                src={img}
                                alt={`Item ${idx + 1} - Photo ${imgIdx + 1}`}
                                className="w-16 h-16 object-cover rounded border-2 border-amber-300 cursor-pointer hover:border-amber-500 transition-all hover:scale-105"
                                onClick={() => setPreviewImage(img)}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {item.internal_remark && (
                        <div className="border-t border-amber-300 pt-2 mt-2">
                          <p className="text-gray-600 font-medium">Internal Remarks:</p>
                          <p className="font-medium text-red-700 mt-1">{item.internal_remark}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Rates Info */}
            <div className="premium-card border-2 border-amber-200 p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-gray-800">Rates & Policy</h3>
                <button 
                  onClick={() => {
                    setShowReviewPage(false);
                    scrollToSection(ratesRef, 'rates');
                  }}
                  className="text-xs text-amber-600 font-semibold hover:text-amber-700"
                >
                  ✏️ Edit
                </button>
              </div>
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Rate Policy:</span>
                  <span className="font-semibold">{ratePolicy === 'lock-today' ? '🔒 Locked Today' : ratePolicy === 'rate-at-delivery' ? '📅 Rate at Delivery' : 'Not Set'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax Scope:</span>
                  <span className="font-semibold">{taxScope}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">GST:</span>
                  <span className="font-semibold">{gstApplicable ? `${gstPercent}% (${gstInputStyle})` : 'Not Applicable'}</span>
                </div>
                {rateMode === 'single' && base24kRate && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">24K Base Rate:</span>
                      <span className="font-semibold text-green-700">₹{base24kRate}/g</span>
                    </div>
                    {Object.keys(purityRates).length > 0 && (
                      <div className="bg-amber-50 rounded p-2 mt-2">
                        <p className="font-semibold text-gray-700 mb-1 text-xs">Purity Rates:</p>
                        {Object.entries(purityRates).map(([purity, rate]) => (
                          <div key={purity} className="flex justify-between text-xs">
                            <span className="text-gray-600">{purity}:</span>
                            <span className="font-medium">₹{parseFloat(rate).toFixed(2)}/g</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Payment Summary */}
            <div className="premium-card border-2 border-green-200 p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-gray-800">Payments & Deposits ({payments.length})</h3>
                <button 
                  onClick={() => {
                    setShowReviewPage(false);
                    scrollToSection(paymentRef, 'payment');
                  }}
                  className="text-xs text-amber-600 font-semibold hover:text-amber-700"
                >
                  ✏️ Edit
                </button>
              </div>
              {payments.length > 0 ? (
                <div className="space-y-2">
                  {payments.map((payment, idx) => {
                    const paymentType = payment.payment_type || payment.mode || 'Cash';
                    return (
                      <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="font-semibold text-sm">{paymentType}</span>
                            {payment.date && (
                              <span className="text-gray-600 ml-2">
                                • {new Date(payment.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            {payment.amount && <span className="font-bold text-green-700">₹{parseFloat(payment.amount).toLocaleString('en-IN')}</span>}
                            {payment.fine_wt && <span className="font-bold text-amber-700 ml-2">{payment.fine_wt}g</span>}
                          </div>
                        </div>
                        {payment.note && <p className="text-gray-600 mt-1">Note: {payment.note}</p>}
                        {payment.reference && <p className="text-gray-600 mt-1">Ref: {payment.reference}</p>}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No advance payments added</p>
              )}
              <div className="mt-3 pt-3 border-t border-gray-300">
                <div className="flex justify-between text-sm">
                  <span className="font-semibold">Total Advance:</span>
                  <span className="font-bold text-green-700">₹{totalAdvance.toLocaleString('en-IN')}</span>
                </div>
                {metalAdvance > 0 && (
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-600">Metal Credit:</span>
                    <span className="font-semibold text-amber-700">{metalAdvance.toFixed(3)}g fine</span>
                  </div>
                )}
              </div>
            </div>

            {/* Signatures */}
            <div className="premium-card border-2 border-blue-200 p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-gray-800">Signatures</h3>
                <button 
                  onClick={() => {
                    setShowReviewPage(false);
                    toggleSection('signatures');
                  }}
                  className="text-xs text-amber-600 font-semibold hover:text-amber-700"
                >
                  ✏️ Edit
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-600 mb-2">Management Signature</p>
                  {managementSignature ? (
                    <div className="border-2 border-gray-200 rounded-lg p-2 bg-white">
                      <img src={managementSignature} alt="Management Signature" className="w-full h-20 object-contain" />
                      {managementSignatureName && (
                        <p className="text-xs text-center text-gray-600 mt-1 border-t pt-1">{managementSignatureName}</p>
                      )}
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <p className="text-xs text-gray-500">Not signed</p>
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-2">Customer Signature</p>
                  {customerSignature ? (
                    <div className="border-2 border-gray-200 rounded-lg p-2 bg-white">
                      <img src={customerSignature} alt="Customer Signature" className="w-full h-20 object-contain" />
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <p className="text-xs text-gray-500">Not signed</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-amber-200 p-4 shadow-lg">
              <div className="flex gap-3 max-w-3xl mx-auto">
                <button
                  onClick={() => setShowReviewPage(false)}
                  className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold"
                >
                  Back to Edit
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating Approval...' : 'Finalize Approval'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Signature Modals */}
      <SignatureModal
        isOpen={showManagementSignatureModal}
        onClose={() => setShowManagementSignatureModal(false)}
        onSave={(signatureData) => {
          setManagementSignature(signatureData);
          setShowManagementSignatureModal(false);
          toast.success('Management signature saved!');
        }}
        title="Management Signature"
        existingSignature={managementSignature}
      />

      <SignatureModal
        isOpen={showCustomerSignatureModal}
        onClose={() => setShowCustomerSignatureModal(false)}
        onSave={(signatureData) => {
          setCustomerSignature(signatureData);
          setShowCustomerSignatureModal(false);
          toast.success('Customer signature saved!');
        }}
        title="Customer Signature"
        existingSignature={customerSignature}
      />

      {/* Old Signature Modal - Keep for backward compatibility */}
      {showSignatureModal && (
        <SignatureModal
          onClose={() => setShowSignatureModal(false)}
          onComplete={handleSignaturesComplete}
        />
      )}

      {/* Edit Customer Modal */}
      {showEditCustomerModal && editingCustomer && (
        <EditCustomerModal
          customer={editingCustomer}
          onClose={() => {
            setShowEditCustomerModal(false);
            setEditingCustomer(null);
          }}
          onCustomerUpdated={() => {
            setShowEditCustomerModal(false);
            setEditingCustomer(null);
            loadCustomers();
          }}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && createdApprovalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Approval Created!</h2>
            <p className="text-gray-600 mb-1">Approval ID: <span className="font-semibold">{createdApprovalData.approval_id}</span></p>
            <p className="text-gray-600 mb-6">Customer: <span className="font-semibold">{createdApprovalData.customer_name}</span></p>
            <button
              onClick={handleSuccessClose}
              className="w-full py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-bold hover:shadow-lg transition-all"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateApprovalStepper;
